import React, { useEffect } from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { useLocation, useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";
import queryString from "query-string";

import { formatGraphQLProductInfoForLegacy } from "shared/payment/helpers/formatGraphQLProductInfoForLegacy";
import _str from "shared/util/_str";
import Modal from "shared/components/Modal";
import Str from "shared/components/Str";
import Strf from "shared/components/Strf";
import CloseIcon from "shared/components/svgs/close_x.svg";
import SmallLogo from "shared/components/svgs/logo_small.svg";
import {
	PROMO_IDS,
	viewedRateCardEvent,
	selectedFirstPartyPromoEvent,
} from "./analytics";
import ContinueButton from "./ContinueButton";
import BoostIcon from "./svgs/boost.svg";
import QuestionsIcon from "./svgs/questions.svg";
import SeeMoreIcon from "./svgs/see_more.svg";

import "./AListPremiumUpgradeRateCard.scss";

const PREMIUM_FEATURES = {
	BOOST: {
		id: "boost",
		icon: BoostIcon,
		description: _str("Increase your chances of likes up to 5x with daily Boost"),
	},
	BE_SEEN: {
		id: "be_seen",
		icon: SeeMoreIcon,
		description: _str("See & be seen by the most popular people"),
	},
	QUESTION: {
		id: "question",
		icon: QuestionsIcon,
		description: _str("See all question answers"),
	},
};

export const MUTATION = gql`
	mutation webIncrementRateCardViews($input: BillingUpdateHasSeenRateCardInput!) {
		billingUpdateHasSeenRateCard(input: $input) {
			success
		}
	}
`;

const AListPremiumUpgradeRateCard = ({
	isOpen,
	onClose,
	formattedAdditionalCost,
	formattedTotalCost,
	productPriceInfo,
	openPaymentModal,
	shouldIncrementRateCardView,
	promoId,
	additionalCost,
}) => {
	const [onIncrementRateCardView] = useMutation(MUTATION);
	const history = useHistory();
	const location = useLocation();

	useEffect(() => {
		onIncrementRateCardView({
			variables: {
				input: {
					rateCardId: "UPGRADE_TO_ALIST_PREMIUM",
				},
			},
		});
	}, [onIncrementRateCardView, shouldIncrementRateCardView]);

	useEffect(() => {
		const queryParams = queryString.parse(location.search);

		if (!isOpen && queryParams.cf) {
			delete queryParams.cf;

			history.replace({
				pathname: location.pathname,
				search: queryString.stringify(queryParams),
			});
		}
	}, [history, isOpen, location]);

	useEffect(() => {
		if (isOpen) {
			viewedRateCardEvent(promoId, additionalCost);
		}
	}, [additionalCost, isOpen, promoId]);

	return (
		<Modal
			show={isOpen}
			className="alist-premium-upgrade-ratecard"
			noClose
			onCancel={onClose}
		>
			<div className="alist-premium-upgrade-ratecard-header">
				<span className="alist-premium-upgrade-ratecard-header-title-container">
					<SmallLogo />
					<Str className="alist-premium-upgrade-ratecard-header-title">Premium</Str>
				</span>
				<button
					className="alist-premium-upgrade-ratecard-close-btn"
					onClick={onClose}
				>
					<CloseIcon />
				</button>
			</div>
			<div className="alist-premium-upgrade-ratecard-body">
				<Str className="alist-premium-upgrade-ratecard-subheader">
					Upgrade for even more features
				</Str>
				<table className="alist-premium-upgrade-ratecard-features">
					<tbody>
						{Object.values(PREMIUM_FEATURES).map((feature) => (
							<tr key={feature.id}>
								<td>
									<feature.icon height={28} width={31} />
								</td>
								<td className="alist-premium-upgrade-ratecard-features-description">
									{feature.description}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<div className="alist-premium-upgrade-ratecard-price-info">
				<Strf
					className="alist-premium-upgrade-ratecard-price-info-header"
					vars={{
						cost: (
							<Strf
								vars={{ formattedAdditionalCost }}
								className="alist-premium-upgrade-ratecard-price-info-price">$formattedAdditionalCost/mo
							</Strf>
						),
					}}
				>
					Update for an extra $cost
				</Strf>
				<Strf
					className="alist-premium-upgrade-ratecard-price-info-total"
					vars={{ formattedTotalCost }}
				>
					Total $formattedTotalCost/mo
				</Strf>
			</div>
			<ContinueButton
				onClick={() => {
					onClose();
					selectedFirstPartyPromoEvent(promoId, additionalCost);
				}}
				openPaymentModal={() =>
					openPaymentModal(formatGraphQLProductInfoForLegacy(productPriceInfo))}
				productId={productPriceInfo?.productId}
			/>
		</Modal>
	);
};

AListPremiumUpgradeRateCard.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	formattedAdditionalCost: PropTypes.string.isRequired,
	formattedTotalCost: PropTypes.string.isRequired,
	openPaymentModal: PropTypes.func.isRequired,
	productPriceInfo: PropTypes.object.isRequired,
	shouldIncrementRateCardView: PropTypes.bool,
	promoId: PropTypes.oneOf(Object.values(PROMO_IDS)),
	additionalCost: PropTypes.number.isRequired,
};

AListPremiumUpgradeRateCard.defaultProps = {
	isOpen: false,
	shouldIncrementRateCardView: false,
	promoId: null,
};


export default AListPremiumUpgradeRateCard;



// WEBPACK FOOTER //
// ./src/shared/components/AListPremiumUpgradeRateCard/AListPremiumUpgradeRateCard.jsx